import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { Alert } from '../components/ui.js';

interface Member { id: string; role: string; status: string; invited_email: string; user_email: string | null; joined_at: string | null }

export function OrgSettings() {
  const [orgId, setOrgId] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [msg, setMsg] = useState<string | null>(null);

  const load = async (id: string) => {
    const res = await api.get<{ members: Member[] }>(`/orgs/${id}/members`);
    setMembers(res.body.members);
  };

  const create = async () => {
    setMsg(null);
    try {
      const res = await api.post<{ org: { id: string } }>('/orgs', { name, slug });
      setOrgId(res.body.org.id);
      await load(res.body.org.id);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed');
    }
  };

  const invite = async () => {
    if (!orgId) return;
    setMsg(null);
    try {
      await api.post(`/orgs/${orgId}/invite`, { email: inviteEmail.trim().toLowerCase(), role: inviteRole });
      setMsg(`Invited ${inviteEmail} as ${inviteRole}.`);
      setInviteEmail('');
      await load(orgId);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed');
    }
  };

  useEffect(() => { if (orgId) load(orgId); }, [orgId]);

  if (!orgId) {
    return (
      <div className="panel" style={{ maxWidth: 560 }}>
        <h3 style={{ marginTop: 0 }}>Create an organisation</h3>
        <p className="muted" style={{ fontSize: 13 }}>Organisations enable shared collections, roles, audit logs, and policies.</p>
        {msg && <Alert kind="error">{msg}</Alert>}
        <div className="field"><label>Organisation name</label><input value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="field"><label>Slug</label><input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} placeholder="my-team" /></div>
        <button className="btn" onClick={create}>Create organisation</button>
      </div>
    );
  }

  return (
    <div className="grid">
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Members</h3>
        {members.length === 0 ? <p className="muted">No members.</p> : (
          <table>
            <thead><tr><th>Email</th><th>Role</th><th>Status</th></tr></thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}><td>{m.user_email ?? m.invited_email}</td><td><span className="badge muted">{m.role}</span></td><td><span className={`badge ${m.status === 'active' ? 'success' : 'warn'}`}>{m.status}</span></td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Invite member</h3>
        {msg && <Alert kind="info">{msg}</Alert>}
        <div className="field"><label>Email</label><input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} /></div>
        <div className="field"><label>Role</label><select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}><option value="member">Member</option><option value="admin">Admin</option></select></div>
        <button className="btn" onClick={invite}>Send invite</button>
      </div>
    </div>
  );
}
